import React, { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, MenuItem, TextField, Typography } from '@mui/material';
import { useMeetingHubContext } from '../../../MeetingHubContext';
import MeetingService from 'services/MeetingService';
import Modal from 'components/shared/Modal';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import { ModalProps } from 'store/types/ComponentProps';
import { Attendee } from 'store/types/Meeting';
import { defaultSnackbarErrorProps } from 'util/layout';

interface PresidingChairModalProps extends ModalProps {
  onSubmit: () => void;
}

const getAttendeeOptions = (attendees: Attendee[]) =>
  attendees?.map((a) => (
    <MenuItem key={a.id} value={a.id}>
      {`${a.name} (${a.affiliate})`}
    </MenuItem>
  ));

const PresidingChairModal: React.FunctionComponent<PresidingChairModalProps> = ({ onClose, open, onSubmit }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, attendees, meetingSession } = useMeetingHubContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [presidingChairId, setPresidingChairId] = useState('');

  const attendeeOptions = useMemo(
    () =>
      getAttendeeOptions(
        attendees?.filter((a) => a.id !== meetingSession?.presidingChairId && a.affiliate?.toLowerCase() !== 'semi') ||
          []
      ),
    [attendees, meetingSession?.presidingChairId]
  );

  const handleClose = useCallback(() => {
    setPresidingChairId('');
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    setLoading(true);
    MeetingService.saveMeetingSession({
      ...meetingSession,
      meetingId: meetingId,
      presidingChairId: presidingChairId,
    })
      .then(() => {
        enqueueSnackbar(`Updated Presiding Chair`, { variant: 'success' });
        setPresidingChairId('');
        onSubmit();
      })
      .catch((error: string) => {
        enqueueSnackbar(`Failed to set Presiding Chair`, defaultSnackbarErrorProps);
        console.error('Error setting Presiding Chair:', error);
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar, meetingId, meetingSession, onSubmit, presidingChairId]);

  return (
    <Modal
      title={'Set Presiding Chair'}
      open={open}
      maxWidth={'xs'}
      loading={loading}
      onClose={handleClose}
      onSubmit={handleSubmit}
      actions={
        <>
          <Button color={'secondary'} variant={'outlined'} disabled={loading} onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" color={'primary'} variant={'contained'}>
            Save
          </Button>
        </>
      }
    >
      <GridContainer>
        <GridItem>
          <Typography variant="subtitle2" color="textSecondary">
            {'Note: Leave attendee select empty to clear presiding chair.'}
          </Typography>
        </GridItem>
        <GridItem>
          <TextField
            label={'Select Attendee'}
            select
            value={presidingChairId}
            onChange={(event) => {
              setPresidingChairId(event.target.value);
            }}
          >
            <MenuItem value={''}>-- None --</MenuItem>
            {attendeeOptions}
          </TextField>
        </GridItem>
      </GridContainer>
    </Modal>
  );
};

export default PresidingChairModal;
